import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { ROLE_LABELS, STATE_STYLE, SEGMENT_STYLE, fmtDate } from "../lib/states";
import LeadDrawer from "../components/LeadDrawer";
import { Users2, CalendarCheck, CheckCircle2, Trophy, XCircle, Flame } from "lucide-react";

export default function Performance() {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/users").then((r) => {
      const team = r.data.filter((u) => u.role !== "admin");
      setUsers(team);
      if (team[0]) setUserId(team[0].id);
    });
  }, []);

  useEffect(() => {
    if (!userId) return;
    api.get(`/performance?user_id=${userId}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [userId]);

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="h-16 shrink-0 px-6 flex items-center justify-between border-b border-stone-200">
        <div>
          <h1 className="font-heading text-xl tracking-tight text-stone-900">By Executive</h1>
          <p className="text-xs text-stone-500">Current workload & efficacy per team member</p>
        </div>
        <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="performance-user-select"
          className="border border-stone-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {ROLE_LABELS[u.role]}</option>)}
        </select>
      </div>

      {data && (
        <div className="p-6 space-y-6">
          {/* Funnel */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            <Kpi icon={Users2} label="Leads handled" value={data.handled} />
            <Kpi icon={CalendarCheck} label="Visits booked" value={data.visit_booked} />
            <Kpi icon={CheckCircle2} label="Visits done" value={data.visit_done} sub={`${data.visit_done_rate}% of booked`} />
            <Kpi icon={Trophy} label="Won" value={data.won} sub={`${data.win_rate}% win rate`} />
            <Kpi icon={XCircle} label="Lost" value={data.lost} />
            <Kpi icon={Flame} label="Marked hot" value={data.hot} />
          </div>

          {/* Current leads */}
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-stone-200">
              <p className="text-sm font-semibold text-stone-800">Current leads ({data.current_leads.length})</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-[0.15em] uppercase text-stone-400 border-b border-stone-200">
                  <th className="px-5 py-2">Lead</th>
                  <th className="px-5 py-2">Stage</th>
                  <th className="px-5 py-2">Segment</th>
                  <th className="px-5 py-2">Role</th>
                  <th className="px-5 py-2">Next action</th>
                </tr>
              </thead>
              <tbody data-testid="performance-leads">
                {data.current_leads.map((l) => (
                  <tr key={l.id} onClick={() => setActive(l)} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer">
                    <td className="px-5 py-2.5 font-medium text-stone-800 flex items-center gap-1.5">
                      {l.is_hot && <Flame className="h-3.5 w-3.5 text-red-500" />}{l.name}
                    </td>
                    <td className="px-5 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATE_STYLE[l.state]?.badge}`}>{l.state}</span></td>
                    <td className="px-5 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full border ${SEGMENT_STYLE[l.segment] || SEGMENT_STYLE.Unknown}`}>{l.segment || "Unknown"}</span></td>
                    <td className="px-5 py-2.5 text-stone-500 capitalize">{l.role_on_lead}</td>
                    <td className="px-5 py-2.5 text-stone-500">{l.next_action_date ? fmtDate(l.next_action_date) : "—"}</td>
                  </tr>
                ))}
                {data.current_leads.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-stone-400">No active leads for this member.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {active && <LeadDrawer leadId={active.id} onClose={() => setActive(null)} onSaved={() => userId && api.get(`/performance?user_id=${userId}`).then((r) => setData(r.data))} />}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs tracking-[0.12em] uppercase font-semibold text-stone-400">{label}</p>
        <Icon className="h-4 w-4 text-stone-400" />
      </div>
      <p className="font-heading text-2xl text-stone-900 mt-1.5" data-testid={`perf-${label}`}>{value}</p>
      {sub && <p className="text-[11px] text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}
